import type { TagStatus } from '@indeed/ifl-components'
import type {
  SchedulingPlan,
  SchedulingPlanLine,
} from 'syft-acp-core/store/agency-cascade-scheduling-plans/types'

export const authoredReasonLabels: Record<SchedulingPlan['authored_reason'], string> = {
  accepted: 'Agency Accepted',
  'agency_shifts.created': 'Shift added',
  // Written when a shift update changes an active line's deadline. flex-core recomputes every line from
  // the job's current settings, so a block booking change can be recorded under this reason instead.
  'shifts.updated': 'Booking deadline changed',
  'shifts.deleted': 'Shift removed',
  // Written when a job stops or starts being a block booking, so the label names neither direction.
  'job.bookable_individually_updated': 'Block booking changed',
  // Written by the dev-tools task that recomputes a plan on request, not by a change to the job or its shifts.
  'dev_tools.plan_recomputed': 'Manual recalculation',
}

export const lineStateLabels: Record<SchedulingPlanLine['state'], string> = {
  scheduled: 'Scheduled',
  not_enforceable: 'Not enforceable',
  met: 'Met',
  missed: 'Missed',
  cascaded: 'Cascaded',
  removed_by_admin: 'Removed by admin',
  released_by_agency: 'Released by agency',
  removed_by_client: 'Removed by client',
  shift_cancelled: 'Shift cancelled',
  revoked: 'Revoked',
}

/** Every removal cause shares one colour: the allocation is gone, whoever took it. */
export const lineStateTagStatuses: Record<SchedulingPlanLine['state'], TagStatus> = {
  scheduled: 'info',
  not_enforceable: 'neutral',
  met: 'success',
  missed: 'critical',
  cascaded: 'warning',
  removed_by_admin: 'warning',
  released_by_agency: 'warning',
  removed_by_client: 'warning',
  shift_cancelled: 'warning',
  revoked: 'warning',
}

/**
 * Readable form of a wire value that has no label yet, e.g. `skipped_past` → "Skipped past" and
 * `agency_shifts.deleted` → "Agency shifts deleted". flex-core can add values ahead of ACP, and stores
 * event names as plan reasons, so lookups fall back to this instead of rendering nothing.
 */
export const humanizeWireValue = (value: string) => {
  const words = value.replace(/[_.]/g, ' ').trim()

  return words.charAt(0).toUpperCase() + words.slice(1)
}

const labelOrHumanized = (labels: Record<string, string>, value: string) =>
  labels[value] ?? humanizeWireValue(value)

export const getAuthoredReasonLabel = (reason: string) => labelOrHumanized(authoredReasonLabels, reason)

export const getLineStateLabel = (state: string) => labelOrHumanized(lineStateLabels, state)

export const getLineStateTagStatus = (state: string): TagStatus =>
  (lineStateTagStatuses as Record<string, TagStatus>)[state] ?? 'neutral'

export const MET_LATER_UNFILLED_LABEL = 'Met - later unfilled'
export const MET_LATER_UNFILLED_TOOLTIP =
  'The booking deadline was met, but the shift has since lost workers.'

/**
 * A met deadline stays met in flex-core, but `workers_provided` is the live count, so the shift can
 * have dropped below its allocation since. The cascade does not revisit met lines, so ACP flags it.
 */
export const isMetLaterUnfilled = (
  line: Pick<SchedulingPlanLine, 'state' | 'workers_provided' | 'allocation_without_buffer'>,
) => line.state === 'met' && line.workers_provided < line.allocation_without_buffer
